import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import ExcelJS from 'exceljs';
import reportService from '../../services/reportService';
import { getWarehouses } from '../../services/warehousesService';
import { authService } from '../../services/authService';
import { filterWarehousesForUser } from '../../utils/limitedUserAccess';
import Toast from '../../components/Common/Toast';
import {
  formatMoney,
  formatQty,
  formatPct,
  formatShortDate,
} from './InventoryDashboard/shared';

const defaultReferenceDate = () => new Date().toISOString().slice(0, 10);

const EntryChangeAlerts = () => {
  const [warehouses, setWarehouses] = useState([]);
  const [referenceDate, setReferenceDate] = useState(defaultReferenceDate);
  const [warehouseId, setWarehouseId] = useState('');
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const hasLoadedWarehouses = useRef(false);

  useEffect(() => {
    if (!hasLoadedWarehouses.current) {
      hasLoadedWarehouses.current = true;
      getWarehouses({ active_only: true })
        .then((r) => {
          if (r.success && r.data) {
            setWarehouses(filterWarehousesForUser(r.data, authService.getCurrentUser()));
          }
        })
        .catch(() => {});
      loadReport(defaultReferenceDate(), '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadReport = async (date = referenceDate, wh = warehouseId) => {
    setLoading(true);
    try {
      const params = { reference_date: date, limit: 500 };
      if (wh) params.warehouse_id = wh;
      const res = await reportService.getEntryChangeAlertsReport(params);
      if (res.success) {
        setRows(res.data || []);
        setMeta(res.meta || null);
      } else {
        setToast({ isOpen: true, message: res.message || 'Error al cargar', type: 'error' });
      }
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al cargar el reporte',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConsult = () => loadReport(referenceDate, warehouseId);

  const exportExcel = async () => {
    if (!rows.length) return;
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Entradas con cambio');
    const showWarehouse = meta?.detail_by_warehouse;
    const headers = showWarehouse
      ? ['Almacén', 'SKU', 'Producto', 'Unidad', 'Día anterior', 'Cant. anterior', 'Día actual', 'Cant. actual', 'Variación', 'Variación %', 'Valor actual S/']
      : ['SKU', 'Producto', 'Unidad', 'Día anterior', 'Cant. anterior', 'Día actual', 'Cant. actual', 'Variación', 'Variación %', 'Valor actual S/'];
    ws.addRow(headers);
    rows.forEach((row) => {
      const base = [
        row.sku || '',
        row.name,
        row.unit_of_measure || '',
        row.previous_date,
        row.previous_qty,
        row.current_date,
        row.current_qty,
        row.change_qty,
        row.change_ratio_pct != null ? row.change_ratio_pct : '',
        row.current_value,
      ];
      ws.addRow(showWarehouse ? [row.warehouse_code || row.warehouse_name || '', ...base] : base);
    });
    ws.getRow(1).font = { bold: true };
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Entradas_cambio_${referenceDate}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const showWarehouse = meta?.detail_by_warehouse;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-6">
        <div className="max-w-[1400px] mx-auto">
          <nav className="text-sm text-gray-600 mb-3">
            <Link to="/dashboard" className="hover:text-teal-600">Dashboard</Link>
            <span className="mx-2">/</span>
            <Link to="/warehouses" className="hover:text-teal-600">Almacenes</Link>
            <span className="mx-2">/</span>
            <span className="text-gray-800">Entradas con mayor cambio</span>
          </nav>

          <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Entradas con mayor cambio</h1>
              <p className="text-gray-600 mt-1 max-w-3xl">
                Detecta productos cuyas entradas aumentaron de forma relevante respecto al día anterior
                (ej. ayer 10 → hoy 50 unidades). No incluye devoluciones.
              </p>
            </div>
            {rows.length > 0 && (
              <button
                type="button"
                onClick={exportExcel}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-800 hover:bg-gray-50"
              >
                Exportar Excel
              </button>
            )}
          </div>

          <div className="bg-white rounded-lg shadow-md p-5 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Fecha de referencia (día actual)</label>
                <input
                  type="date"
                  value={referenceDate}
                  onChange={(e) => setReferenceDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Almacén</label>
                <select
                  value={warehouseId}
                  onChange={(e) => setWarehouseId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="">Todos (detalle por almacén)</option>
                  {warehouses.map((w) => (
                    <option key={w.id} value={w.id}>{w.code} — {w.name}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2 flex items-end">
                <button
                  type="button"
                  onClick={handleConsult}
                  disabled={loading}
                  className="w-full md:w-auto px-6 py-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white rounded-md font-medium text-sm"
                >
                  {loading ? 'Consultando...' : 'Consultar'}
                </button>
              </div>
            </div>
            {meta && (
              <p className="text-sm text-gray-600">
                Comparando <strong>{formatShortDate(meta.reference_date)}</strong> vs{' '}
                <strong>{formatShortDate(meta.previous_date)}</strong>
                {' · '}{meta.total} alerta{meta.total === 1 ? '' : 's'}
              </p>
            )}
            <div className="flex flex-wrap gap-2 text-sm mt-3">
              <Link to="/reports/inventory-dashboard" className="text-teal-600 hover:underline">Dashboard de inventario</Link>
              <span className="text-gray-300">·</span>
              <Link to="/reports/entries-exits" className="text-teal-600 hover:underline">Entradas y salidas</Link>
            </div>
          </div>

          {loading ? (
            <div className="bg-white rounded-lg shadow-md p-12 text-center text-gray-500">Cargando...</div>
          ) : rows.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-10 text-center">
              <p className="text-gray-600">No hay aumentos significativos de entradas para la fecha seleccionada.</p>
              <p className="text-sm text-gray-500 mt-2">
                Se alerta si el aumento es ≥ 50%, ≥ 5 unidades, o entrada ≥ 10 desde cero.
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-teal-700 text-white text-left text-xs uppercase tracking-wide">
                    <tr>
                      {showWarehouse && <th className="px-4 py-3 font-semibold">Almacén</th>}
                      <th className="px-4 py-3 font-semibold">Producto</th>
                      <th className="px-4 py-3 font-semibold text-right">Día anterior</th>
                      <th className="px-4 py-3 font-semibold text-right">Cant. anterior</th>
                      <th className="px-4 py-3 font-semibold text-right">Día actual</th>
                      <th className="px-4 py-3 font-semibold text-right">Cant. actual</th>
                      <th className="px-4 py-3 font-semibold text-right">Variación</th>
                      <th className="px-4 py-3 font-semibold text-right">Valor actual</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {rows.map((row) => (
                      <tr key={`${row.product_id}-${row.warehouse_id || 0}`} className="hover:bg-amber-50/40">
                        {showWarehouse && (
                          <td className="px-4 py-2.5 text-gray-700 whitespace-nowrap">
                            {row.warehouse_code || row.warehouse_name || '—'}
                          </td>
                        )}
                        <td className="px-4 py-2.5">
                          <div className="font-medium text-gray-900">{row.name}</div>
                          {row.sku ? <div className="text-xs text-gray-500">{row.sku}</div> : null}
                        </td>
                        <td className="px-4 py-2.5 text-right text-gray-600 whitespace-nowrap">
                          {formatShortDate(row.previous_date)}
                        </td>
                        <td className="px-4 py-2.5 text-right whitespace-nowrap">
                          {formatQty(row.previous_qty)} {row.unit_of_measure || ''}
                        </td>
                        <td className="px-4 py-2.5 text-right text-gray-600 whitespace-nowrap">
                          {formatShortDate(row.current_date)}
                        </td>
                        <td className="px-4 py-2.5 text-right font-semibold text-amber-900 whitespace-nowrap">
                          {formatQty(row.current_qty)} {row.unit_of_measure || ''}
                        </td>
                        <td className="px-4 py-2.5 text-right whitespace-nowrap">
                          <span className="font-semibold text-amber-800">+{formatQty(row.change_qty)}</span>
                          {row.change_ratio_pct != null && (
                            <span className="text-xs text-amber-700 ml-1">({formatPct(row.change_ratio_pct)})</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-right whitespace-nowrap">{formatMoney(row.current_value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default EntryChangeAlerts;
